import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { StaffWeek } from '@components/pages/Settings/Staff/WorkSchedule/staffShifts.util'

const ENAMEL_AI_LOGO_PATH = '/logo512.png'

export interface WorkScheduleDayHeader {
  name: string
  date: string
}

export interface WorkSchedulePdfParams {
  clinicName: string
  clinicAddress: string
  rangeLabel: string
  dayHeaders: WorkScheduleDayHeader[]
  staffWeeks: StaffWeek[]
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function workSchedulePdfFilename(rangeLabel: string): string {
  const base = rangeLabel.replace(/[^a-z0-9]/gi, '_').toLowerCase()
  return `work_schedule_${base}.pdf`
}

function resolveLogoUrl(): string {
  if (typeof window === 'undefined') return ENAMEL_AI_LOGO_PATH
  return new URL(ENAMEL_AI_LOGO_PATH, window.location.origin).href
}

async function loadLogoAsDataUrl(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  try {
    const res = await fetch(resolveLogoUrl(), { cache: 'force-cache' })
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = (): void => {
        const r = reader.result
        resolve(typeof r === 'string' ? r : '')
      }
      reader.onerror = (): void =>
        reject(reader.error ?? new Error('FileReader failed'))
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

function buildHeaderHtml(
  clinicName: string,
  clinicAddress: string,
  logoSrc: string,
): string {
  const addressBlock =
    clinicAddress.trim().length > 0
      ? `<div class="ws-pdf-header-clinic-address">${escapeHtml(clinicAddress.trim())}</div>`
      : ''
  return `<div class="ws-pdf-header">
  <img src="${logoSrc}" alt="EnamelAI logo" class="ws-pdf-header-logo" />
  <div class="ws-pdf-header-text">
    <div class="ws-pdf-header-clinic-name">${escapeHtml(clinicName)}</div>
    ${addressBlock}
  </div>
</div>`
}

function buildMetaHtml(rangeLabel: string): string {
  return `<div class="ws-pdf-meta">
  <div class="ws-pdf-meta-title">Work Schedule</div>
  <div class="ws-pdf-meta-range">${escapeHtml(rangeLabel)}</div>
</div>`
}

function buildTableHtml(
  dayHeaders: WorkScheduleDayHeader[],
  staffWeeks: StaffWeek[],
): string {
  const headCells = dayHeaders
    .map(
      (d) => `<th class="ws-pdf-th">
        <div class="ws-pdf-th-name">${escapeHtml(d.name)}</div>
        <div class="ws-pdf-th-date">${escapeHtml(d.date)}</div>
      </th>`,
    )
    .join('')

  const bodyRows =
    staffWeeks.length === 0
      ? `<tr><td class="ws-pdf-td ws-pdf-td-empty" colspan="${dayHeaders.length + 1}">No staff found.</td></tr>`
      : staffWeeks
          .map((s) => {
            const cells = s.shifts
              .map((sh) => {
                const overrideHtml = sh.overrideLabel
                  ? `<div class="ws-pdf-override-time">${escapeHtml(sh.overrideLabel)}</div>`
                  : ''
                return `<td class="ws-pdf-td ws-pdf-td-${sh.kind}">${escapeHtml(sh.label)}${overrideHtml}</td>`
              })
              .join('')
            return `<tr>
              <td class="ws-pdf-td ws-pdf-td-staff">${escapeHtml(s.staffName)}</td>
              ${cells}
            </tr>`
          })
          .join('')

  return `<table class="ws-pdf-table">
    <thead>
      <tr>
        <th class="ws-pdf-th ws-pdf-th-staff">Staff members</th>
        ${headCells}
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>`
}

function buildCaptureHtml(
  params: WorkSchedulePdfParams,
  logoSrc: string,
): string {
  return `${buildHeaderHtml(params.clinicName, params.clinicAddress, logoSrc)}${buildMetaHtml(params.rangeLabel)}${buildTableHtml(params.dayHeaders, params.staffWeeks)}`
}

export async function downloadWorkScheduleAsPdf(
  params: WorkSchedulePdfParams,
): Promise<void> {
  const logoSrc = (await loadLogoAsDataUrl()) ?? resolveLogoUrl()

  const wrapper = document.createElement('div')
  wrapper.className = 'ws-pdf-export-root'
  wrapper.setAttribute('aria-hidden', 'true')
  Object.assign(wrapper.style, {
    position: 'fixed',
    left: '-9999px',
    top: '0',
    width: '1123px',
    zIndex: '-1',
    pointerEvents: 'none',
    backgroundColor: '#ffffff',
  })
  wrapper.innerHTML = buildCaptureHtml(params, logoSrc)
  document.body.appendChild(wrapper)

  try {
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), 300)
    })

    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      width: wrapper.scrollWidth,
      height: wrapper.scrollHeight,
    })

    if (canvas.width <= 0 || canvas.height <= 0) {
      throw new Error('Invalid canvas size for PDF generation.')
    }

    const pdf = new jsPDF('l', 'pt', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 32

    const imgWidth = pageWidth - margin * 2
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let yPos = margin
    let remainingHeight = imgHeight
    let sourceY = 0
    const sourceHeight = canvas.height

    while (remainingHeight > 0) {
      if (
        yPos + Math.min(remainingHeight, pageHeight - yPos - margin) >
        pageHeight - margin
      ) {
        pdf.addPage()
        yPos = margin
      }

      const availableHeight = pageHeight - yPos - margin
      const heightToAdd = Math.min(remainingHeight, availableHeight)
      if (heightToAdd <= 0) {
        if (availableHeight <= 0) {
          pdf.addPage()
          yPos = margin
          continue
        }
        break
      }

      const sourceHeightToUse = (heightToAdd / imgHeight) * sourceHeight
      if (sourceHeightToUse <= 0) break

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = sourceHeightToUse
      const ctx = pageCanvas.getContext('2d')
      if (!ctx) break

      ctx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sourceHeightToUse,
        0,
        0,
        canvas.width,
        sourceHeightToUse,
      )
      const pageImgData = pageCanvas.toDataURL('image/jpeg', 1.0)
      const pageImgHeight = (sourceHeightToUse * imgWidth) / canvas.width
      if (pageImgHeight <= 0 || !Number.isFinite(pageImgHeight)) break

      pdf.addImage(pageImgData, 'JPEG', margin, yPos, imgWidth, pageImgHeight)
      yPos += pageImgHeight
      sourceY += sourceHeightToUse
      remainingHeight -= heightToAdd
    }

    pdf.save(workSchedulePdfFilename(params.rangeLabel))
  } finally {
    wrapper.remove()
  }
}
